/**
 * Reporting periods.
 *
 * A clinic asks the same question at four zoom levels — this month, this
 * quarter, this half, this year — and the answer is only comparable if every
 * screen means the same thing by "quarter". So the boundaries are computed in
 * one place, from calendar dates, and both the API and the interface use it.
 *
 * Dates are plain YYYY-MM-DD strings in the CLINIC's calendar, not instants. A
 * quarter is a range of days; turning it into a timestamp is the database's job
 * and it does that in the clinic's own timezone.
 */

export type PeriodGrain = 'month' | 'quarter' | 'half' | 'year';

export interface PeriodRange {
  grain: PeriodGrain;
  /** Inclusive first day, YYYY-MM-DD. */
  from: string;
  /** Inclusive last day, YYYY-MM-DD. */
  to: string;
  /** A stable key, e.g. `2026-Q3`. Safe to use as a React key or a cache key. */
  key: string;
}

const MONTHS_PER: Record<PeriodGrain, number> = { month: 1, quarter: 3, half: 6, year: 12 };

const pad = (n: number) => String(n).padStart(2, '0');
const lastDayOf = (year: number, month1: number) => new Date(Date.UTC(year, month1, 0)).getUTCDate();

/** The period of the given grain that contains `on` (default: today). */
export function periodContaining(grain: PeriodGrain, on: string = today()): PeriodRange {
  const year = Number(on.slice(0, 4));
  const month1 = Number(on.slice(5, 7));
  const span = MONTHS_PER[grain];
  // Snap to the start of the block: month 5 in a quarter grain starts at 4.
  const startMonth = Math.floor((month1 - 1) / span) * span + 1;
  const endMonth = startMonth + span - 1;
  return {
    grain,
    from: `${year}-${pad(startMonth)}-01`,
    to: `${year}-${pad(endMonth)}-${pad(lastDayOf(year, endMonth))}`,
    key: keyFor(grain, year, startMonth),
  };
}

/** `count` consecutive periods ending with the one containing `on`, oldest first. */
export function recentPeriods(grain: PeriodGrain, count: number, on: string = today()): PeriodRange[] {
  if (count < 1) return [];
  const out: PeriodRange[] = [];
  const span = MONTHS_PER[grain];
  const current = periodContaining(grain, on);
  let year = Number(current.from.slice(0, 4));
  let month1 = Number(current.from.slice(5, 7));
  for (let i = 0; i < count; i += 1) {
    const endMonth = month1 + span - 1;
    out.unshift({
      grain,
      from: `${year}-${pad(month1)}-01`,
      to: `${year}-${pad(endMonth)}-${pad(lastDayOf(year, endMonth))}`,
      key: keyFor(grain, year, month1),
    });
    month1 -= span;
    if (month1 < 1) {
      month1 += 12;
      year -= 1;
    }
  }
  return out;
}

/** The calendar months in a range, oldest first, as `YYYY-MM`. */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let year = Number(from.slice(0, 4));
  let month1 = Number(from.slice(5, 7));
  const endKey = to.slice(0, 7);
  // Bounded: a decade of months is far more than any statement asks for, and it
  // makes a malformed range fail loudly instead of spinning.
  for (let guard = 0; guard < 240; guard += 1) {
    const key = `${year}-${pad(month1)}`;
    if (key > endKey) break;
    out.push(key);
    month1 += 1;
    if (month1 > 12) {
      month1 = 1;
      year += 1;
    }
  }
  return out;
}

/** First and last day of a `YYYY-MM` month. */
export function monthRange(month: string): { from: string; to: string } {
  const year = Number(month.slice(0, 4));
  const month1 = Number(month.slice(5, 7));
  return { from: `${month}-01`, to: `${month}-${pad(lastDayOf(year, month1))}` };
}

/** Today in UTC, as YYYY-MM-DD. Callers with a clinic timezone should pass their own. */
export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function keyFor(grain: PeriodGrain, year: number, startMonth: number): string {
  if (grain === 'year') return String(year);
  if (grain === 'quarter') return `${year}-Q${Math.floor((startMonth - 1) / 3) + 1}`;
  if (grain === 'half') return `${year}-H${Math.floor((startMonth - 1) / 6) + 1}`;
  return `${year}-${pad(startMonth)}`;
}
